import debounce from 'lodash/debounce';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useState, useMemo, useCallback, useLayoutEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import * as mealActions from '~/store/legacy/actions/mealActions.js';
import { addToCart as addToCartAction } from '~/store/legacy/actions/shoppingCartActions';
import CategoryTabs from '~/components/CategoryTabs.jsx';
import MealDetail from '~/components/MealDetail.jsx';
import CategoryMeals from '~/components/menu/CategoryMeals';
import JsonLDRestaurant from '~/components/menu/JsonLDRestaurant';
import RestaurantInfo from '~/components/menu/RestaurantInfo';
import RestaurantTabs from '~/components/RestaurantTabs.jsx';
import { SERVICE_TYPE } from '~/constant.js';
import {
  checkDeliveryRestOpen,
  selectDeliveryGroupByRestaurantId,
} from '~/store/legacy/selector/deliverySelectors.js';
import {
  selectCategoriedMealsByRestaurant,
  selectCategoryByRestaurant,
  selectMealCustomization,
  selectMealInventory,
  selectMealsCombined,
  selectRestaurants,
} from '~/store/legacy/selector/mealSelectors.js';
import { selectUserAddress, selectUserServiceType } from '~/store/legacy/selector/userSelectors.js';
import { linkToRestaurant } from '~/utils/restaurant.js';
import { PAGE_META_DESCRIPTION } from '~/constant'

function isRestOpen({ restaurant, deliveryGroup }) {
  if (!restaurant || !deliveryGroup) {
    return true;
  }
  const { isRestOpen } = checkDeliveryRestOpen({ restaurant, deliveryGroup });
  return isRestOpen;
}

const visibleCategories = new Set();

function DeliveryRestaurantMenu(props) {
  const restaurant = props.restaurant;
  const restaurantId = restaurant.id;
  const mealId = props.mealId;
  const router = useRouter();
  const dispatch = useDispatch();
  const [category, setCategory] = useState(null);
  const { address } = useSelector(selectUserAddress);
  const deliveryGroup = useSelector(selectDeliveryGroupByRestaurantId(restaurantId));
  const serviceType = useSelector(selectUserServiceType);
  const restaurantIds = deliveryGroup
    ? deliveryGroup.relationships.restaurants.data.map((restaurant) => restaurant.id)
    : [restaurantId];
  const restaurants = useSelector(selectRestaurants(restaurantIds));
  const categories = useSelector(selectCategoryByRestaurant(restaurantId), (objA, objB) => {
    if (!Array.isArray(objA) || !Array.isArray(objB) || objA.length !== objB.length) {
      return false;
    }
    let objAIds = objA.map((item) => item.data.id).sort().join(',');
    let objBIds = objB.map((item) => item.data.id).sort().join(',');
    if (objAIds !== objBIds) {
      return false;
    }
    return true;
  });
  const allMeals = useSelector(selectMealsCombined, (objA, objB) => {
    if (Object.keys(objA).length !== Object.keys(objB).length) {
      return false;
    }
    return true;
  });


  // const selectCategoriedMealsByRestaurantMemoized = useMemo(() => selectCategoriedMealsByRestaurant(restaurantId), [restaurantId]);
  const categoriedMeals = useSelector(selectCategoriedMealsByRestaurant(restaurantId), (objA, objB) => {
    if (Object.keys(objA).length !== Object.keys(objB).length) {
      return false;
    }
    return true;
  });
  const customizations = useSelector(selectMealCustomization);
  const inventory = useSelector(selectMealInventory);
  const [selectedMeal, setSelectedMeal] = useState(null);


  useLayoutEffect(() => {
    if (mealId && allMeals[mealId]) {
      // selectMeal(allMeals[mealId]);
      setSelectedMeal(allMeals[mealId]);
    } else {
      setSelectedMeal(null);
    }
  }, [allMeals, mealId]);

  useEffect(() => {
    if (!category && categories.length) {
      setCategory(categories[0]);
    }
  }, [categories]);

  const selectMeal = useCallback((meal) => {
    if (meal) {
      const link = linkToRestaurant(
        SERVICE_TYPE.DELIVERY,
        restaurant.id,
        restaurant.attributes.name,
        meal.id
      );
      router.replace(link.href, link.as, { shallow: true, scroll: false });
      // setSelectedMeal(meal);
    }
  }, [restaurant]);

  const visibilityChange = useCallback((categoryId, visible) => {
    visible ? visibleCategories.add(categoryId) : visibleCategories.delete(categoryId);

    let loadMissingData = debounce(() => {
      const mealIds = Array.from(visibleCategories).reduce((acc, categoryId) => {
        // in case if it is loading.
        if (!categoriedMeals[categoryId]) {
          return acc;
        }
        return acc.concat(...categoriedMeals[categoryId].map((meal) => meal.id));
      }, []);
      const missedMealInventory = mealIds.filter((mealId) => !inventory[mealId]);
      if (missedMealInventory.length > 0) {
        dispatch(mealActions.loadMealInventory({ mealIds: missedMealInventory }));
      }
      const missedMealCustomizations = mealIds.filter((mealId) => !customizations[mealId]);
      if (missedMealCustomizations.length > 0) {
        dispatch(mealActions.loadMealCustomizations({ mealIds: missedMealCustomizations }));
      }
      visibleCategories.clear();
    }, 200);

    loadMissingData();

  }, [categoriedMeals]);

  const changeCategory = (categoryId) => {
    const category = categories.find((category) => category.data.id === categoryId);
    if (category) {
      setCategory(category);
      const el = document.getElementById(`category_${category.data.id}`);
      if (el) {
        window.scrollTo({
          behavior: 'smooth',
          left: 0,
          top: el.offsetTop - 75,
        });
      }
    }
  };

  const unSelectMeal = useCallback(() => {
    const link = linkToRestaurant(SERVICE_TYPE.DELIVERY, restaurant.id, restaurant.attributes.name);
    router.replace(link.href, link.as, { shallow: true, scroll: false });
    // setSelectedMeal(null);
  }, [restaurant]);

  const backToRestaurants = useCallback(() => {
    router.replace(
      `/delivery/[address]`,
      `/delivery/${address.address_1 ? address.address_1 : address.city}`
    );
  }, [address]);

  const addToCart = (meal, mealNote) => {
    dispatch(addToCartAction({ meal, mealNote, deliveryGroup, serviceType, router }));
  };

  const changeRestaurant = useCallback((restaurantId) => {
    const restaurant = restaurants.find((restaurant) => restaurant.id === restaurantId);
    if (restaurant) {
      const link = linkToRestaurant(
        SERVICE_TYPE.DELIVERY,
        restaurant.id,
        restaurant.attributes.name
      );
      router.replace(link.href, link.as);
    }
  }, [restaurants]);

  return (
    <>
      <Head>
        <title>
          {restaurant.attributes.name} {restaurant.relationships.address.data.attributes.address1}
        </title>
        <meta name='description' content={`${PAGE_META_DESCRIPTION} `} />
      </Head>
      <JsonLDRestaurant restaurant={restaurant} />
      <RestaurantInfo
        restaurant={restaurant}
        isRestOpen={isRestOpen({ restaurant: restaurant, deliveryGroup })}
      />
      <RestaurantTabs
        restaurantId={restaurant.id}
        restaurants={restaurants.filter((restaurant) => isRestOpen({ restaurant, deliveryGroup }))}
        changeRestaurant={changeRestaurant}
      />
      {category && (
        <CategoryTabs
          categoryId={category.data.id}
          categories={categories}
          changeCategory={changeCategory}
        />
      )}
      {categories.map((category) => (
        <CategoryMeals
          selectMeal={selectMeal}
          key={category.data.id}
          restaurant={restaurant}
          visibilityChange={visibilityChange}
          category={category}
          meals={categoriedMeals[category.data.id]}
        />
      ))}
      {selectedMeal ? (
        <MealDetail
          restaurant={restaurant}
          onClose={unSelectMeal}
          addToCart={addToCart}
          deliveryGroup={deliveryGroup}
          meal={selectedMeal}
          isRestaurantOpen={isRestOpen({ restaurant: restaurant, deliveryGroup })}
          backToRestaurants={backToRestaurants}
        />
      ) : null}
    </>
  );
}

export default DeliveryRestaurantMenu;
